import React, { useState, useMemo, useEffect } from 'react';
import Icon from '@mdi/react';
import { mdiLinkVariant, mdiCheckCircle, mdiAlertCircle, mdiOpenInNew, mdiInformationOutline } from '@mdi/js';
import webApiClient from '../../api/client';
import { countriesForCurrency } from '../../config/payoutCountries';
import './ConnectOnboarding.css';

/**
 * ConnectOnboarding — Shows Stripe Connect status and onboarding CTA.
 *
 * States:
 *   - not_connected: Show payout-country picker + "Set Up Payouts" button
 *   - restricted (onboarding incomplete): Show "Complete Verification" button
 *   - active: Show success banner
 *
 * `baseCurrency` (the org's base currency) determines which countries can be the
 * payout-account country. The country is chosen here at link time, NOT at
 * registration, because the account country is "where your bank account is" and
 * a currency does not imply a single country (e.g. EUR spans many).
 */
// Stripe's hosted onboarding returns the user here after they finish (or bail).
const RETURN_PATH = '/manage-finances?onboarded=true';
const REFRESH_PATH = '/manage-finances?refresh=true';

// No onStatusChange callback: every action here leaves the page for Stripe's hosted
// flow, and the refresh on return is owned by ManageFinances' post-onboarding poll.
function ConnectOnboarding({ connectStatus, organisationId, baseCurrency, compact = false }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // When an onboarding link is created, we keep it so a visible "Continue to
  // Stripe" link is always available even if the auto-redirect is interrupted
  // (popup blockers, extensions, tracking protection) — the user is never stranded.
  const [onboardingUrl, setOnboardingUrl] = useState(null);

  const countryOptions = useMemo(() => countriesForCurrency(baseCurrency), [baseCurrency]);
  const [payoutCountry, setPayoutCountry] = useState('');

  // Auto-select when the currency maps to exactly one country (e.g. NZD -> NZ);
  // otherwise leave blank so the user consciously picks. This runs as an effect
  // (not lazy useState) because baseCurrency arrives asynchronously after mount —
  // a one-shot initial state would lock in the default-currency country (AUD/AU)
  // before the real currency loads and never correct itself.
  useEffect(() => {
    if (countryOptions.length === 1) {
      setPayoutCountry(countryOptions[0].code);
    }
  }, [countryOptions]);

  const handleConnect = async () => {
    if (!payoutCountry) {
      setError('Please select the country where your payout bank account is located.');
      return;
    }
    setLoading(true);
    setError(null);
    setOnboardingUrl(null);
    try {
      const returnUrl = `${window.location.origin}${RETURN_PATH}`;
      const refreshUrl = `${window.location.origin}${REFRESH_PATH}`;

      const res = await webApiClient.post('/api/v2/payment/connect/onboard', {
        organisationId,
        returnUrl,
        refreshUrl,
        payoutCountry,
      });

      if (res.data.accountLink) {
        // Redirect this tab straight to Stripe (not window.open — that is
        // popup-blocked after an await). We do NOT set state before redirecting:
        // doing so re-renders the card into its "continue" view for the instant
        // before navigation lands, flashing a stray button on screen. The happy
        // path navigates away cleanly. As a robustness fallback, if navigation
        // has NOT happened ~4s later (blocked / odd browser), surface the
        // visible "Continue to Stripe" link so the user is never stranded.
        const link = res.data.accountLink;
        setTimeout(() => setOnboardingUrl(link), 4000);
        window.location.assign(link);
      } else {
        // The call SUCCEEDED but returned no link — a backend contract violation, not a
        // network blip, and a different fault from the catch below even though both show
        // the same sentence. Log the payload: this branch previously discarded it, so a
        // recurrence left nothing to diagnose. (Seen once 2026-09-07; the retry worked,
        // which fits the known v2 account create-then-link timing issue in
        // LESSONS-LEARNED L-2026-06-26 — but with no record it could not be confirmed.)
        console.error('Connect onboarding: 200 with no accountLink in response', res.data);
        setError('We could not start Stripe verification. Please try again.');
      }
    } catch (err) {
      console.error('Connect onboarding error:', err);
      // Surface the backend's actual reason (e.g. "A NZD payout account must be
      // in a country that settles NZD…") rather than a generic message.
      setError(err?.data?.message || 'We could not start Stripe verification. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleRefreshLink = async () => {
    setLoading(true);
    setError(null);
    setOnboardingUrl(null);
    try {
      const returnUrl = `${window.location.origin}${RETURN_PATH}`;
      const refreshUrl = `${window.location.origin}${REFRESH_PATH}`;

      const res = await webApiClient.post('/api/v2/payment/connect/refresh-link', {
        organisationId,
        returnUrl,
        refreshUrl,
      });

      if (res.data.accountLink) {
        // Redirect straight to Stripe; delayed fallback link if navigation does
        // not happen. See handleConnect for the rationale.
        const link = res.data.accountLink;
        setTimeout(() => setOnboardingUrl(link), 4000);
        window.location.assign(link);
      } else {
        setError('We could not open Stripe verification. Please try again.');
      }
    } catch (err) {
      console.error('Refresh link error:', err);
      setError(err?.data?.message || 'We could not open Stripe verification. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!connectStatus) return null;

  const handleManageAccount = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await webApiClient.post('/api/v2/payment/connect/login-link', {
        organisationId,
      });
      if (res.data.url) {
        window.open(res.data.url, '_blank', 'noopener,noreferrer');
      }
    } catch (err) {
      console.error('Login link error:', err);
      setError('Failed to open account management. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Once an onboarding link exists, the account is created and the next step is
  // for the user to finish Stripe's hosted verification. We show this as a
  // user-clicked link (rather than auto-redirecting) so a browser that breaks
  // Stripe's page can never leave the user on a blank screen with no way back.
  const continueBlock = onboardingUrl && (
    <div className="connect-fallback">
      <p className="connect-continue-prompt">
        We've started setting up your payout account with Stripe.
        Continue to Stripe to add your identity and bank details and finish verification.
        You will not receive payouts until this step is complete.
      </p>
      <a
        className="connect-action-btn connect-primary connect-fallback-link"
        href={onboardingUrl}
        target="_blank"
        rel="noopener noreferrer"
      >
        <Icon path={mdiOpenInNew} size={0.8} />
        <span>Continue to Stripe verification</span>
      </a>
      <p className="connect-fallback-hint">
        If the Stripe page does not open or looks blank, try opening it in another browser.
      </p>
    </div>
  );

  // Active — all set
  if (connectStatus.status === 'active') {
    const maskedId = connectStatus.accountId
      ? `••••${connectStatus.accountId.slice(-6)}`
      : null;

    return (
      <div className="connect-status-banner connect-active">
        <div className="connect-status-left">
          <Icon path={mdiCheckCircle} size={1} />
          <div className="connect-status-content">
            <p className="connect-status-title">Payment account active</p>
            {connectStatus.displayName && (
              <p className="connect-status-desc">{connectStatus.displayName}</p>
            )}
            {maskedId && (
              <p className="connect-status-account-id">Account {maskedId}</p>
            )}
            {error && <p className="connect-error-text">{error}</p>}
          </div>
        </div>
        <button
          className="connect-action-btn"
          onClick={handleManageAccount}
          disabled={loading}
        >
          <Icon path={mdiOpenInNew} size={0.8} />
          <span>{loading ? 'Loading...' : 'Manage Payout Account'}</span>
        </button>
      </div>
    );
  }

  // Restricted — onboarding started but incomplete
  if (connectStatus.status === 'restricted') {
    return (
      <div className="connect-status-banner connect-restricted">
        <div className="connect-status-left">
          <Icon path={mdiAlertCircle} size={1} />
          <div className="connect-status-content">
            <p className="connect-status-title">Verification required</p>
            <p className="connect-status-desc">
              Almost there. Finish your ID and bank account verification to start receiving payments.
            </p>
            {error && <p className="connect-error-text">{error}</p>}
            {continueBlock}
          </div>
        </div>
        <button
          className="connect-action-btn"
          onClick={handleRefreshLink}
          disabled={loading}
        >
          <Icon path={mdiOpenInNew} size={0.8} />
          <span>{loading ? 'Loading...' : 'Complete Verification'}</span>
        </button>
      </div>
    );
  }

  const currencyCode = String(baseCurrency || 'AUD').toUpperCase();

  const countryHint = (
    <p className="form-hint">
      Your base currency is {currencyCode}, so your bank must be in a country that supports it.
      If that is wrong, change Base Currency in Settings &gt; Account Details.
    </p>
  );

  const countryPicker = (
    <div className="connect-payout-country">
      <label htmlFor="connect-payout-country" className="form-label">
        Which country is your bank account in?
      </label>
      <select
        id="connect-payout-country"
        className="form-select"
        value={payoutCountry}
        onChange={(e) => setPayoutCountry(e.target.value)}
        disabled={loading}
      >
        <option value="">Select a country</option>
        {countryOptions.map((c) => (
          <option key={c.code} value={c.code}>{c.label}</option>
        ))}
      </select>
    </div>
  );

  const linkButton = (
    <button
      className="connect-action-btn connect-primary"
      onClick={handleConnect}
      disabled={loading || !payoutCountry}
    >
      <Icon path={mdiLinkVariant} size={0.85} />
      <span>{loading ? 'Linking...' : 'Link Bank Account'}</span>
    </button>
  );

  // Compact (inside the Manage Finances accordion) — no card chrome, icon tile,
  // intro, note, or tip box; the accordion header already carries the icon, title,
  // and the explanatory subtitle. Body is just the labelled field + action.
  if (compact) {
    return (
      <div className="connect-onboarding-compact">
        {onboardingUrl ? (
          // Account created — show the Continue step, not the create form again
          // (re-submitting would create a duplicate account).
          continueBlock
        ) : (
          <>
            <div className="connect-compact-cols">
              <div className="connect-compact-copy">{countryHint}</div>
              <div className="connect-compact-field">{countryPicker}</div>
            </div>
            {error && <p className="connect-error-text">{error}</p>}
            <div className="connect-compact-action">{linkButton}</div>
          </>
        )}
      </div>
    );
  }

  // Not connected (standalone hero)
  return (
    <div className="connect-onboarding-card">
      <div className="connect-onboarding-left">
        <div className="connect-onboarding-icon">
          <Icon path={mdiLinkVariant} size={1.5} />
        </div>
        <div className="connect-onboarding-content">
          <h3 className="connect-onboarding-title">Link your bank account</h3>
          <p className="connect-onboarding-desc">
            Add your bank details to receive automatic payouts when students book lessons or buy tickets.
            Students pay all fees, so you receive your full asking price.
          </p>
          <p className="connect-onboarding-note">
            No existing Stripe account needed. We'll guide you through a short ID and bank verification (powered by Stripe).
          </p>
          <p className="connect-onboarding-tip">
            <Icon path={mdiInformationOutline} size={0.6} style={{ verticalAlign: 'middle', marginRight: '4px' }} />
            <strong>Tip:</strong> Stripe will ask for a website. If you don't have one, just use <strong>dancelocale.com</strong>
          </p>
          {onboardingUrl ? (
            continueBlock
          ) : (
            <>
              {countryPicker}
              {countryHint}
              {error && <p className="connect-error-text">{error}</p>}
            </>
          )}
        </div>
      </div>
      {!onboardingUrl && linkButton}
    </div>
  );
}

export default ConnectOnboarding;
